import React, { useEffect, useContext, useRef, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import api from "./Utils/api";
import "./App.css";
import "./styles/styles.css";
import { GlobalContext, GlobalProvider } from "./Utils/GlobalContext";
import { can } from "./Utils/controlaPermissao";
import { FeedbackProvider } from "./components/Generals/Feedback/FeedbackContext";

import Login from "./components/Login/Login";
import MenuSide from "./components/Menu/MenuSide";
import MenuTop from "./components/Menu/MenuTop";
import Usuarios from "./components/Usuarios/Usuarios";
import GrupoUsuario from "./components/GruposUsuario/GruposUsuario";
import TiposDocumento from "./components/TiposDocumento/TiposDocumento";
import ClassificacaoTiposDocumento from "./components/ClassificacaoTiposDocumento/ClassificacaoTiposDocumento";
import TiposFornecedor from "./components/TiposFornecedor/TiposFornecedor";
import Empresas from "./components/Empresas/Empresas";
import IndicesCorrecao from "./components/IndicesCorrecao/IndicesCorrecao";
import ConfiguracoesCampos from "./components/Configuracoes/ConfiguracoesCampos/ConfiguracoesCampos";
import EmailNotificacao from "./components/Configuracoes/EmailNotificacao/EmailNotificacao";
import TiposPagamento from "./components/TiposPagamento/TiposPagamento";
import Documentos from "./components/Documentos/Documentos";
import TiposAgendamento from "./components/TiposAgendamento/TiposAgendamento";
import Agendamentos from "./components/Agendamentos/Agendamentos";
import CamposCustomizados from "./components/CamposCustomizados/CamposCustomizados";
import Funcionarios from "./components/Funcionarios/Funcionarios";
import Estatisticas from "./components/PainelGestao/Estatisticas";
import GodMode from "./components/GodMode/GodMode";
import PainelConsolidadoDocumentos from "./components/PainelGestao/PainelConsolidadoDocumentos";
import RedirectToDefault from "./components/PageHeader/RedirectToDefault";
import Precos from "./components/Precos/Precos";
import AtivacaoEntidade from "./components/Login/AtivacaoEntidade";
import AceitarConvite from "./components/Login/AceitarConvite";
import MensalidadePendenteAviso from "./components/Mensalidade/MensalidadePendenteAviso";
import MensalidadePendenteBloqueio from "./components/Mensalidade/MensalidadePendenteBloqueio";
import AutoConfiguracaoAssistente from "./components/AutoConfiguracao/AutoConfiguracaoAssistente";
import ContatoFlutuante from "./components/Contato/ContatoFlutuante";

// O cliente HTTP central (instância `api`) e seus interceptors — auth,
// refresh-token silencioso, tratamento de 401 e evento de mensalidade em
// atraso — vivem em `src/Utils/api.js`. Importar `api` aqui (e nos demais
// componentes) garante que os interceptors sejam registrados uma única vez.

// ------- helpers -------
const isAuthenticated = () => {
  const token = localStorage.getItem("authToken");
  return token !== null && token !== undefined && token !== "";
};

// Espera o carregamento das permissões (contexto ou localStorage).
// Evita negar acesso antes de carregar os dados.
const usePermsReady = () => {
  const { globalData } = useContext(GlobalContext);
  return Boolean(
    globalData?.permissoes ||
      globalData?.permissoes_grupo ||
      localStorage.getItem("permissoes_grupo")
  );
};

const todasEtapasAutoConfiguracaoResolvidas = (status) =>
  Array.isArray(status?.etapas) &&
  status.etapas.every((etapa) => etapa.concluida);

const TIMEOUT_AUTOCONFIGURACAO_STATUS_MS = 15000;
const TIMEOUT_CONFIGURACAO_ENTIDADE_MS = 10000;

// Wrapper: exige estar autenticado (layout protegido com Menu)
const ProtectedRoute = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { globalData, menuType } = useContext(GlobalContext);
  const isSuperior = menuType === "superior";
  const entidadeIdRaw = globalData?.entidade_id ?? localStorage.getItem("entidade_id");
  const entidadeId =
    entidadeIdRaw && Number.isInteger(Number(entidadeIdRaw)) && Number(entidadeIdRaw) > 0
      ? Number(entidadeIdRaw)
      : null;
  const deveValidarAutoConfiguracao = isAuthenticated() && Boolean(entidadeId);
  const [validandoAutoConfiguracao, setValidandoAutoConfiguracao] = useState(true);
  const [autoConfiguracaoPendente, setAutoConfiguracaoPendente] = useState(false);

  useEffect(() => {
    if (!isAuthenticated()) navigate("/login");
    document.body.classList.remove("login-page-body");
    document.body.classList.toggle("menu-superior", isSuperior);

    if (isSuperior) {
      document.body.classList.remove("sidebar-collapsed");
    } else {
      const isCollapsed = JSON.parse(
        localStorage.getItem("sidebarCollapsed") || "false"
      );
      if (isCollapsed) document.body.classList.add("sidebar-collapsed");
      else document.body.classList.remove("sidebar-collapsed");
    }
  }, [navigate, isSuperior]);

  useEffect(() => {
    if (!deveValidarAutoConfiguracao) {
      setValidandoAutoConfiguracao(false);
      setAutoConfiguracaoPendente(false);
      return;
    }

    let ativo = true;
    setValidandoAutoConfiguracao(true);
    const controller = new AbortController();
    const timeoutId = setTimeout(
      () => controller.abort(),
      TIMEOUT_AUTOCONFIGURACAO_STATUS_MS
    );

    api
      .get(`/api/autoconfiguracao/status`, {
        headers: { id_entidade: entidadeId },
        signal: controller.signal,
      })
      .then(({ data }) => {
        if (!ativo) return;
        const status = data?.data || null;
        setAutoConfiguracaoPendente(!todasEtapasAutoConfiguracaoResolvidas(status));
      })
      .catch((err) => {
        if (!ativo) return;
        setAutoConfiguracaoPendente(false);
        console.error("[AutoConfiguracao] Erro ao validar bloqueio de rota:", err);
      })
      .finally(() => {
        clearTimeout(timeoutId);
        if (ativo) setValidandoAutoConfiguracao(false);
      });

    return () => {
      ativo = false;
      clearTimeout(timeoutId);
      controller.abort();
    };
  }, [deveValidarAutoConfiguracao, entidadeId, location.pathname]);

  if (!isAuthenticated()) return null;
  if (validandoAutoConfiguracao) return null;
  if (
    autoConfiguracaoPendente &&
    location.pathname.toLowerCase() !== "/autoconfiguracao"
  ) {
    return <Navigate to="/AutoConfiguracao" replace />;
  }

  return (
    <>
      {isSuperior ? <MenuTop /> : <MenuSide />}
      <div className="main-content">
        <Outlet />
      </div>
      {location.pathname.toLowerCase() !== "/autoconfiguracao" && <ContatoFlutuante />}
    </>
  );
};

// Wrapper: exige uma permissão específica
const RequirePermission = ({ perm, children }) => {
  const { globalData } = useContext(GlobalContext);
  const ready = usePermsReady();

  if (!ready) return null; // pode renderizar um spinner se preferir
  const allowed = can(globalData, perm);
  return allowed ? children : <Navigate to="/PaginaInicial" replace />;
};

// Wrapper: exige que pelo menos 1 permissão da lista seja "S"
/*
const RequireAny = ({ perms = [], children }) => {
  const { globalData } = useContext(GlobalContext);
  const ready = usePermsReady();

  if (!ready) return null;
  const allowed = perms.some((p) => can(globalData, p));
  return allowed ? children : <Navigate to="/PaginaInicial" replace />;
};
*/

// Wrapper específico para rota /Empresas:
// valida conforme a classificação salva (V=empresas, E=entidades, F=fornecedores)
const RequireEmpresasByClass = ({ children }) => {
  const cls = localStorage.getItem("classificacao"); // "V" | "E" | "F"
  const map = { V: "empresas", E: "entidades", F: "fornecedores" };
  const permKey = map[cls] || "empresas";
  return <RequirePermission perm={permKey}>{children}</RequirePermission>;
};

// ---- AppInner: usa o contexto (já envolvido pelo GlobalProvider) ----
function AppInner() {
  const authenticated = isAuthenticated();
  const { globalData, setGlobalData } = useContext(GlobalContext);
  const userId = globalData?.user_id; // 👈 extraído para resolver warning
  const entidadeId = globalData?.entidade_id;
  const permissoesFetchedForUserId = useRef(null);

  useEffect(() => {
    if (!authenticated || !userId || !entidadeId) {
      permissoesFetchedForUserId.current = null;
      return;
    }
    // Rastreia userId + entidadeId: re-busca quando a entidade mudar (mesmo usuário)
    const fetchKey = `${userId}:${entidadeId}`;
    if (permissoesFetchedForUserId.current === fetchKey) return;
    permissoesFetchedForUserId.current = fetchKey;

    const token = localStorage.getItem("authToken");

    const LOG_ATIVO = (process.env.REACT_APP_CONSOLE_LOG || "").trim() === 'S';

    const fetchPermissoes = async () => {
      // log incondicional: confirma que fetchPermissoes foi chamado e qual o valor do env
      console.log('[GDoc] fetchPermissoes iniciado | REACT_APP_CONSOLE_LOG =', JSON.stringify((process.env.REACT_APP_CONSOLE_LOG || "").trim()), '| userId =', userId, '| entidadeId =', entidadeId);

      try {
        const { data } = await api.get(
          `/api/gruposusuario/PermissoesPorUsuario/${userId}`,
          {
            params: { id_entidade: entidadeId },
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        // Normaliza: backend/DB pode retornar "funcionario" (singular), mas usamos "funcionarios"
        const perms = { ...(data?.permissoes ?? data) };
        if (perms.funcionario !== undefined && perms.funcionarios === undefined) {
          perms.funcionarios = perms.funcionario;
          delete perms.funcionario;
        }
        const dataNormalizada = { ...data, permissoes: perms };
        const superAdmin = data?.super_admin === 'S' ? 'S' : 'N';
        const mensalidadeAbonada = data?.mensalidade_abonada === 'S' ? 'S' : 'N';

        if (LOG_ATIVO) {
          console.group('%c[GDoc] Sessão iniciada', 'color:#6366f1;font-weight:bold;font-size:13px');
          console.log('user_id     :', userId);
          console.log('super_admin :', superAdmin === 'S' ? '✅ SIM' : '❌ NÃO');
          console.log('tipo        :', data?.tipo ?? '—');
          console.log('permissoes  :', perms);
          console.log('empresas_acesso :', data?.empresas_acesso ?? []);
          console.log('tipos_doc_acesso:', data?.tipos_documento_acesso ?? []);
          console.groupEnd();
        }

        setGlobalData((prev) => ({
          ...prev,
          permissoes_grupo: dataNormalizada,
          super_admin: superAdmin,
          mensalidade_abonada: mensalidadeAbonada,
        }));
      } catch (err) {
        console.error("[App] Erro ao buscar PermissoesPorUsuario:", err?.response?.data ?? err?.message ?? err);
        if (LOG_ATIVO) {
          console.warn('[GDoc] ⚠️ fetchPermissoes falhou — verifique se o backend está rodando e se as migrações SQL foram executadas.');
        }
        // Erros 401 são tratados pelo interceptor (redireciona para login).
        // Para qualquer outro erro, define permissoes_grupo como objeto vazio para que
        // usePermsReady() retorne true e o app não fique preso em estado "carregando".
        const status = err?.response?.status;
        if (status !== 401) {
          setGlobalData((prev) => ({
            ...prev,
            permissoes_grupo: prev.permissoes_grupo ?? { permissoes: {} },
          }));
        }
        permissoesFetchedForUserId.current = null;
      }
    };

    fetchPermissoes();
  }, [authenticated, userId, entidadeId, setGlobalData]);

  // Item #34 — situação financeira da entidade (mensalidade pendente/bloqueio).
  // Mesmo padrão/gatilho do fetch de permissões; nunca persistido em localStorage
  // (deve ser revalidado no backend a cada bootstrap, não confiar em estado local).
  const situacaoFetchedForKey = useRef(null);
  useEffect(() => {
    if (!authenticated || !userId || !entidadeId) {
      situacaoFetchedForKey.current = null;
      return;
    }
    const fetchKey = `${userId}:${entidadeId}`;
    if (situacaoFetchedForKey.current === fetchKey) return;
    situacaoFetchedForKey.current = fetchKey;

    const token = localStorage.getItem("authToken");

    const fetchSituacaoFinanceira = async () => {
      try {
        const { data } = await api.get(
          `/api/assinaturas/situacao-financeira`,
          {
            params: { id_entidade: entidadeId },
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        setGlobalData((prev) => ({ ...prev, situacaoFinanceira: data?.data ?? null }));
      } catch (err) {
        // 403 MENSALIDADE_EM_ATRASO (se algum dia esta própria rota entrar em uma
        // whitelist diferente) é tratado pelo interceptor global; qualquer outro
        // erro aqui não deve travar a experiência do usuário.
        console.error(
          "[App] Erro ao buscar situacao-financeira:",
          err?.response?.data ?? err?.message ?? err
        );
        situacaoFetchedForKey.current = null;
      }
    };

    fetchSituacaoFinanceira();
  }, [authenticated, userId, entidadeId, setGlobalData]);

  // Item #36 (LGPD) — configuração da entidade ativa, disponível a qualquer tela
  // via globalData.lgpdOcultarDados sem cada uma precisar buscar por conta própria.
  // Mesmo padrão/gatilho do fetch de situação financeira; nunca persistido em
  // localStorage. Falha (erro de rede, config ausente) assume oculto (falha segura).
  const lgpdFetchedForKey = useRef(null);
  useEffect(() => {
    if (!authenticated || !userId || !entidadeId) {
      lgpdFetchedForKey.current = null;
      return;
    }
    const fetchKey = `${userId}:${entidadeId}`;
    if (lgpdFetchedForKey.current === fetchKey) return;
    lgpdFetchedForKey.current = fetchKey;

    const token = localStorage.getItem("authToken");

    const fetchLgpdConfig = async () => {
      let timeoutId;
      try {
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), TIMEOUT_CONFIGURACAO_ENTIDADE_MS);
        const { data } = await api.get(
          `/api/empresasConfiguracoes/listarConfiguracoes/${entidadeId}`,
          {
            params: { incluir_logotipo: "N" },
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
            signal: controller.signal,
          }
        );
        setGlobalData((prev) => ({ ...prev, lgpdOcultarDados: data?.lgpd_ocultar_dados === "S" }));
      } catch (err) {
        console.error(
          "[App] Erro ao buscar configuração LGPD:",
          err?.response?.data ?? err?.message ?? err
        );
        setGlobalData((prev) => ({ ...prev, lgpdOcultarDados: true }));
        lgpdFetchedForKey.current = null;
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };

    fetchLgpdConfig();
  }, [authenticated, userId, entidadeId, setGlobalData]);

  // Escuta o evento disparado pelo interceptor quando qualquer chamada retorna
  // 403 MENSALIDADE_EM_ATRASO no meio de uma sessão já aberta.
  useEffect(() => {
    const handler = (ev) => {
      setGlobalData((prev) => ({
        ...prev,
        situacaoFinanceira: {
          ...(prev.situacaoFinanceira || {}),
          temPendencia: true,
          bloqueado: true,
          diasEmAberto: ev.detail?.diasEmAberto ?? prev.situacaoFinanceira?.diasEmAberto ?? 31,
        },
      }));
    };
    window.addEventListener("mensalidade:bloqueado", handler);
    return () => window.removeEventListener("mensalidade:bloqueado", handler);
  }, [setGlobalData]);

  // Carrega rota_padrao do usuário ao autenticar
  useEffect(() => {
    if (!authenticated || !userId) return;
    const token = localStorage.getItem("authToken");
    const fetchRotaPadrao = async () => {
      try {
        const { data } = await api.get(
          `/api/usuarios/ObterRotaPadrao/${userId}`,
          {
            headers: { Authorization: token ? `Bearer ${token}` : undefined },
          }
        );
        const rota = data?.rota_padrao || null;
        setGlobalData((prev) => ({ ...prev, rota_padrao: rota }));
      } catch {
        setGlobalData((prev) => ({ ...prev, rota_padrao: null }));
      }
    };
    fetchRotaPadrao();
  }, [authenticated, userId, setGlobalData]);

  // Carrega numero_linhas_grid das configurações da empresa quando entidade muda
  useEffect(() => {
    if (!authenticated || !entidadeId) return;
    const token = localStorage.getItem("authToken");
    const fetchConfig = async () => {
      let timeoutId;
      try {
        const controller = new AbortController();
        timeoutId = setTimeout(() => controller.abort(), TIMEOUT_CONFIGURACAO_ENTIDADE_MS);
        const { data } = await api.get(
          `/api/empresasConfiguracoes/listarConfiguracoes/${entidadeId}`,
          {
            params: { incluir_logotipo: "N" },
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              id_entidade: entidadeId,
            },
            signal: controller.signal,
          }
        );
        const valor = Number(data?.numero_linhas_grid) || 10;
        setGlobalData((prev) => ({
          ...prev,
          numero_linhas_grid: valor,
          nome_entidade: data?.nome_entidade || data?.fantasia || data?.razao_social || prev.nome_entidade || null,
        }));

        api
          .get(`/api/empresasConfiguracoes/logotipo/${entidadeId}`, {
            headers: {
              Authorization: token ? `Bearer ${token}` : undefined,
              id_entidade: entidadeId,
            },
            responseType: "blob",
            validateStatus: (status) => status < 500,
          })
          .then((res) => {
            if (res.status === 404 || !res.data || !res.data.size) return;
            const logoUrl = URL.createObjectURL(res.data);
            setGlobalData((prev) => {
              if (typeof prev.logotipo === "string" && prev.logotipo.startsWith("blob:")) {
                URL.revokeObjectURL(prev.logotipo);
              }
              return { ...prev, logotipo: logoUrl };
            });
          })
          .catch(() => {
            setGlobalData((prev) => ({ ...prev, logotipo: null }));
          });
      } catch {
        setGlobalData((prev) => ({ ...prev, numero_linhas_grid: 10 }));
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    };
    fetchConfig();
  }, [authenticated, entidadeId, setGlobalData]);

  // Carrega parâmetros globais do sistema (GOD MODE → leitura pública para auth)
  useEffect(() => {
    if (!authenticated) return;
    const token = localStorage.getItem("authToken");
    api
      .get("/api/parametrosSistema", {
        headers: { Authorization: token ? `Bearer ${token}` : undefined },
        validateStatus: (s) => s < 500,
      })
      .then((r) => {
        const p = r.data?.parametros || {};
        setGlobalData((prev) => ({
          ...prev,
          mostrarColunaId: p.mostrar_ids_exclusao !== "N",
        }));
      })
      .catch(() => {});
  }, [authenticated, setGlobalData]);

  return (
    <Router>
      <MensalidadePendenteAviso />
      <MensalidadePendenteBloqueio />
      <Routes>
        {/* Página pública de preços */}
        <Route path="/precos" element={<Precos />} />
        <Route path="/ativar-entidade" element={<AtivacaoEntidade />} />
        <Route path="/aceitar-convite" element={<AceitarConvite />} />

        {/* Login */}
        <Route
          path="/login"
          element={!authenticated ? <Login /> : <Navigate to="/" replace />}
        />

        {/* Rotas protegidas (layout + auth) */}
        <Route element={<ProtectedRoute />}>
          {/* Livre para qualquer autenticado */}
          <Route path="AutoConfiguracao" element={<AutoConfiguracaoAssistente />} />
          <Route path="PaginaInicial" element={<Navigate to="/Estatisticas" replace />} />
          <Route path="Estatisticas" element={<Estatisticas />} />
          <Route path="GodMode" element={<GodMode />} />
          <Route path="PainelConsolidadoDocumentos" element={<PainelConsolidadoDocumentos />} />

          {/* Cadastros (gated) */}
          <Route
            path="Documentos"
            element={
              <RequirePermission perm="documentos">
                <Documentos />
              </RequirePermission>
            }
          />

          {/* /Empresas respeita a classificação salva (V/E/F) */}
          <Route
            path="Empresas"
            element={
              <RequireEmpresasByClass>
                <Empresas />
              </RequireEmpresasByClass>
            }
          />
          {/* Rotas legadas que apontam para o mesmo componente (opcional manter) */}
          <Route
            path="Entidades"
            element={
              <RequirePermission perm="entidades">
                <Empresas classificacaoOverride="E" />
              </RequirePermission>
            }
          />
          <Route
            path="Fornecedores"
            element={
              <RequirePermission perm="fornecedores">
                <Empresas classificacaoOverride="F" />
              </RequirePermission>
            }
          />
          <Route
            path="Funcionarios"
            element={
              <RequirePermission perm="funcionarios">
                <Funcionarios />
              </RequirePermission>
            }
          />

          <Route
            path="IndicesCorrecao"
            element={
              <RequirePermission perm="indices_correcao">
                <IndicesCorrecao />
              </RequirePermission>
            }
          />
          <Route
            path="TiposDocumento"
            element={
              <RequirePermission perm="tipos_documento">
                <TiposDocumento />
              </RequirePermission>
            }
          />
          <Route
            path="ClassificacaoTiposDocumento"
            element={
              <RequirePermission perm="tipos_documento">
                <ClassificacaoTiposDocumento />
              </RequirePermission>
            }
          />
          <Route
            path="TiposFornecedor"
            element={
              <RequirePermission perm="tipos_empresa">
                <TiposFornecedor />
              </RequirePermission>
            }
          />

          {/* Configurações (gated) */}
          <Route
            path="ConfiguracoesCampos"
            element={
              <RequirePermission perm="configuracoes_campos">
                <ConfiguracoesCampos />
              </RequirePermission>
            }
          />
          <Route
            path="EmailNotificacao"
            element={
              <RequirePermission perm="configuracoes_email">
                <EmailNotificacao />
              </RequirePermission>
            }
          />
          {/* Ações/geração */}
          <Route
            path="GrupoUsuario"
            element={
              <RequirePermission perm="grupos_usuarios">
                <GrupoUsuario />
              </RequirePermission>
            }
          />
          <Route
            path="TiposAgendamento"
            element={
              <RequirePermission perm="tipos_agendamento">
                <TiposAgendamento />
              </RequirePermission>
            }
          />
          <Route
            path="Agendamentos"
            element={
              <RequirePermission perm="gerar_agendamentos">
                <Agendamentos />
              </RequirePermission>
            }
          />

          <Route
            path="CamposCustomizados"
            element={
              <RequirePermission perm="campos_customizados">
                <CamposCustomizados />
              </RequirePermission>
            }
          />

          {/* Usuários */}
          <Route
            path="Usuario"
            element={
              <RequirePermission perm="usuarios">
                <Usuarios />
              </RequirePermission>
            }
          />

           {/* TiposPagamento */}
          <Route
            path="TiposPagamento"
            element={
              <RequirePermission perm="tipos_pagamento">
                <TiposPagamento />
              </RequirePermission>
            }
          />

        </Route>

        {/* Redirecionamentos padrão */}
        <Route
          path="/"
          element={
            authenticated ? (
              <RedirectToDefault />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />
        <Route
          path="*"
          element={
            <Navigate to={authenticated ? "/PaginaInicial" : "/login"} replace />
          }
        />
      </Routes>
    </Router>
  );
}

// ---- App: apenas envolve com o Provider ----
function App() {
  return (
    <GlobalProvider>
      <FeedbackProvider>
        <AppInner />
      </FeedbackProvider>
    </GlobalProvider>
  );
}

export default App;
